import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import PatientLayout from "@/components/patient/PatientLayout";
import WelcomeBanner from '@/components/dashboard/WelcomeBanner';
import QuickStatsWidget, { StatItem } from '@/components/dashboard/QuickStatsWidget';
import NotificationCenter from '@/components/dashboard/NotificationCenter';
import {
  FileText,
  Stethoscope,
  CheckCircle,
  Clock,
  Home,
  Hospital,
  Ambulance,
  UserRound,
  ClipboardList,
  Search,
  CalendarIcon,
  X,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  ArrowRightLeft,
  History,
  Settings
} from "lucide-react";
import { TransferRequestStatus } from "@/components/patient/TransferRequestStatus";
import { TransferRequestDialog } from "@/components/patient/TransferRequestDialog";
import RoleSwitcher from "@/components/admin/RoleSwitcher";

interface TriageSession {
  id: string;
  created_at: string;
  completed_at: string | null;
  recommendation: 'HOME' | 'PCP' | 'URGENT_CARE' | 'ER_NOW' | null;
  score: number | null;
  explanation_text: string | null;
}

export default function PatientDashboardPage() {
  const { user: authUser, loading: authLoading } = useAuth();
  const { user: adminUser } = useAdminAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<TriageSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "completed" | "draft">("all");
  const [recommendationFilter, setRecommendationFilter] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
  const [sortBy, setSortBy] = useState<"date-desc" | "date-asc" | "severity-desc" | "severity-asc">("date-desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [patientData, setPatientData] = useState<{ id: string; agency_id: string; agency_name: string } | null>(null);
  const itemsPerPage = 10;

  // Get effective user (could be admin viewing as patient or actual patient)
  const user = authUser;
  // Show RoleSwitcher for any admin role
  const isAdminViewingAsPatient = adminUser?.role === 'super_admin' || adminUser?.role === 'agency_admin' || adminUser?.role === 'supervisor';

  useEffect(() => {
    const fetchData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Fetch sessions
        const { data: sessionsData, error: sessionsError } = await supabase
          .from("triage_sessions")
          .select("id, created_at, completed_at, recommendation, score, explanation_text")
          .order("created_at", { ascending: false })
          .limit(100);

        if (sessionsError) throw sessionsError;
        setSessions(sessionsData || []);

        // Fetch patient data for transfer feature
        const { data: patientInfo, error: patientError } = await supabase
          .from("patients")
          .select("id, agency_id, agencies(name)")
          .eq("user_id", user.id)
          .single();

        if (!patientError && patientInfo) {
          setPatientData({
            id: patientInfo.id,
            agency_id: patientInfo.agency_id,
            agency_name: (patientInfo.agencies as any)?.name || 'Unknown Agency',
          });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const getRecommendationConfig = (rec: string | null) => {
    switch (rec) {
      case "HOME":
        return { label: "Stay Home", icon: Home, color: "success", bg: "bg-success/10 text-success border-success/20" };
      case "PCP":
        return { label: "See Doctor", icon: UserRound, color: "primary", bg: "bg-primary/10 text-primary border-primary/20" };
      case "URGENT_CARE":
        return { label: "Urgent Care", icon: Hospital, color: "warning", bg: "bg-warning/10 text-warning border-warning/20" };
      case "ER_NOW":
        return { label: "Emergency", icon: Ambulance, color: "destructive", bg: "bg-destructive/10 text-destructive border-destructive/20" };
      default:
        return { label: "Incomplete", icon: Clock, color: "muted", bg: "bg-muted/10 text-muted-foreground border-muted/20" };
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSeverityOrder = (rec: string | null): number => {
    switch (rec) {
      case "ER_NOW": return 4;
      case "URGENT_CARE": return 3;
      case "PCP": return 2;
      case "HOME": return 1;
      default: return 0;
    }
  };

  const filteredSessions = useMemo(() => {
    let result = sessions.filter((session) => {
      if (statusFilter === "completed" && !session.completed_at) return false;
      if (statusFilter === "draft" && session.completed_at) return false;

      if (recommendationFilter !== "all") {
        if (recommendationFilter === "incomplete" && session.recommendation) return false;
        if (recommendationFilter !== "incomplete" && session.recommendation !== recommendationFilter) return false;
      }

      const sessionDate = new Date(session.created_at);
      if (dateFrom) {
        const fromStart = new Date(dateFrom);
        fromStart.setHours(0, 0, 0, 0);
        if (sessionDate < fromStart) return false;
      }
      if (dateTo) {
        const toEnd = new Date(dateTo);
        toEnd.setHours(23, 59, 59, 999);
        if (sessionDate > toEnd) return false;
      }

      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const config = getRecommendationConfig(session.recommendation);
        const dateStr = formatDate(session.created_at).toLowerCase();
        const explanation = (session.explanation_text || "").toLowerCase();
        
        return (
          config.label.toLowerCase().includes(query) ||
          dateStr.includes(query) ||
          explanation.includes(query)
        );
      }

      return true;
    });

    result.sort((a, b) => {
      switch (sortBy) {
        case "date-desc":
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case "date-asc":
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case "severity-desc":
          return getSeverityOrder(b.recommendation) - getSeverityOrder(a.recommendation);
        case "severity-asc":
          return getSeverityOrder(a.recommendation) - getSeverityOrder(b.recommendation);
        default:
          return 0;
      }
    });

    return result;
  }, [sessions, searchQuery, statusFilter, recommendationFilter, dateFrom, dateTo, sortBy]);

  const totalPages = Math.ceil(filteredSessions.length / itemsPerPage);
  const paginatedSessions = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredSessions.slice(start, start + itemsPerPage);
  }, [filteredSessions, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, recommendationFilter, dateFrom, dateTo, sortBy]);

  const completedCount = sessions.filter(s => s.completed_at).length;
  const pendingCount = sessions.filter(s => !s.completed_at).length;

  const clearAllFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setRecommendationFilter("all");
    setDateFrom(undefined);
    setDateTo(undefined);
    setSortBy("date-desc");
  };

  const hasActiveFilters = searchQuery || statusFilter !== "all" || recommendationFilter !== "all" || dateFrom || dateTo || sortBy !== "date-desc";

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-accent flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-accent flex items-center justify-center">
        <Card className="max-w-md mx-auto">
          <CardContent className="p-8 text-center space-y-4">
            <h2 className="text-2xl font-bold">Please Sign In</h2>
            <p className="text-muted-foreground">You need to be logged in to view your dashboard.</p>
            <Button asChild>
              <Link to="/login">Sign In</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <PatientLayout>
      <Helmet>
        <title>Patient Dashboard - CheckFirstCare</title>
        <meta name="description" content="View your health assessment history and recommendations." />
      </Helmet>
      
      <div className="space-y-6">
        {/* Welcome Banner */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1">
            <WelcomeBanner
              role="patient"
              userName={user.patient?.first_name || user.email?.split('@')[0] || 'Patient'}
              userEmail={user.email}
              quickActions={[
                { label: 'Start Assessment', icon: Stethoscope, onClick: () => navigate('/triage') },
                { label: 'View History', icon: History, onClick: () => {}, variant: 'outline' },
                { label: 'Settings', icon: Settings, onClick: () => navigate('/profile/settings'), variant: 'outline' },
              ]}
            />
          </div>
          {/* Always show RoleSwitcher - it will self-hide if user only has patient access */}
          <RoleSwitcher currentView="patient" />
        </div>

        {/* Quick Stats and Notification Center */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
          <div className="lg:col-span-3">
            <QuickStatsWidget 
              stats={[
                { 
                  title: 'Total Assessments', 
                  value: sessions.length, 
                  description: 'All time', 
                  icon: ClipboardList, 
                  color: 'text-primary',
                  onClick: () => { setStatusFilter('all'); }
                },
                { 
                  title: 'Completed', 
                  value: completedCount, 
                  description: 'Finished assessments', 
                  icon: CheckCircle, 
                  color: 'text-success',
                  onClick: () => { setStatusFilter('completed'); }
                },
                { 
                  title: 'Incomplete', 
                  value: pendingCount, 
                  description: 'In progress', 
                  icon: Clock, 
                  color: 'text-warning',
                  onClick: () => { setStatusFilter('draft'); }
                },
              ]} 
              columns={3}
            />
          </div>
          <NotificationCenter role="patient" />
        </div>

          {/* Start New Assessment CTA */}
          <Card className="health-card mb-8 border-primary/20 bg-primary/5">
            <CardContent className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl gradient-hero flex items-center justify-center">
                  <Stethoscope className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Need a health check?</h3>
                  <p className="text-sm text-muted-foreground">Start a new symptom assessment</p>
                </div>
              </div>
              <Button asChild>
                <Link to="/triage">Start Assessment</Link>
              </Button>
            </CardContent>
          </Card>

          {/* Agency Transfer Section */}
          {patientData && (
            <div className="mb-8">
              <TransferRequestStatus
                patientId={patientData.id}
                onRequestTransfer={() => setShowTransferDialog(true)}
              />
            </div>
          )}

          {/* Assessment History */}
          <Card className="health-card">
            <CardHeader>
              <div className="flex flex-col gap-4">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Assessment History
                </CardTitle>
                
                {/* Search and Filters */}
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search assessments..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                    
                    <div className="flex gap-2 flex-wrap">
                      <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as "all" | "completed" | "draft")}>
                        <SelectTrigger className="w-[130px]">
                          <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Status</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="draft">Incomplete</SelectItem>
                        </SelectContent>
                      </Select>

                      <Select value={recommendationFilter} onValueChange={setRecommendationFilter}>
                        <SelectTrigger className="w-[140px]">
                          <SelectValue placeholder="Recommendation" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Results</SelectItem>
                          <SelectItem value="HOME">Stay Home</SelectItem>
                          <SelectItem value="PCP">See Doctor</SelectItem>
                          <SelectItem value="URGENT_CARE">Urgent Care</SelectItem>
                          <SelectItem value="ER_NOW">Emergency</SelectItem>
                          <SelectItem value="incomplete">Incomplete</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Date Range and Sort */}
                  <div className="flex flex-wrap gap-2 items-center">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className={cn("gap-2", dateFrom && "text-primary")}>
                          <CalendarIcon className="h-4 w-4" />
                          {dateFrom ? format(dateFrom, "MMM d, yyyy") : "From date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} />
                      </PopoverContent>
                    </Popover>

                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className={cn("gap-2", dateTo && "text-primary")}>
                          <CalendarIcon className="h-4 w-4" />
                          {dateTo ? format(dateTo, "MMM d, yyyy") : "To date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={dateTo} onSelect={setDateTo} />
                      </PopoverContent>
                    </Popover>

                    <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
                      <SelectTrigger className="w-[160px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="date-desc">
                          <span className="flex items-center gap-2"><ArrowDown className="h-3 w-3" /> Newest First</span>
                        </SelectItem>
                        <SelectItem value="date-asc">
                          <span className="flex items-center gap-2"><ArrowUp className="h-3 w-3" /> Oldest First</span>
                        </SelectItem>
                        <SelectItem value="severity-desc">
                          <span className="flex items-center gap-2"><ArrowDown className="h-3 w-3" /> Highest Severity</span>
                        </SelectItem>
                        <SelectItem value="severity-asc">
                          <span className="flex items-center gap-2"><ArrowUp className="h-3 w-3" /> Lowest Severity</span>
                        </SelectItem>
                      </SelectContent>
                    </Select>

                    {hasActiveFilters && (
                      <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-muted-foreground">
                        <X className="h-4 w-4 mr-1" />
                        Clear all
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {paginatedSessions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {hasActiveFilters ? (
                    <p>No assessments match your filters</p>
                  ) : (
                    <p>No assessments yet. Start your first health check!</p>
                  )}
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    {paginatedSessions.map((session) => {
                      const config = getRecommendationConfig(session.recommendation);
                      const IconComponent = config.icon;
                      
                      return (
                        <Link
                          key={session.id}
                          to={`/assessment/${session.id}`}
                          className="block p-4 rounded-lg border hover:border-primary/50 transition-colors bg-card"
                        >
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center shrink-0", config.bg)}>
                                <IconComponent className="w-5 h-5" />
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-foreground truncate">
                                  {formatDate(session.created_at)}
                                </p>
                                {session.explanation_text && (
                                  <p className="text-sm text-muted-foreground truncate">
                                    {session.explanation_text.slice(0, 60)}...
                                  </p>
                                )}
                              </div>
                            </div>
                            <Badge variant="outline" className={cn("shrink-0", config.bg)}>
                              {config.label}
                            </Badge>
                          </div>
                        </Link>
                      );
                    })}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-6">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        Page {currentPage} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Transfer Dialog */}
        {patientData && (
          <TransferRequestDialog
            open={showTransferDialog}
            onOpenChange={setShowTransferDialog}
            patientId={patientData.id}
            currentAgencyId={patientData.agency_id}
            currentAgencyName={patientData.agency_name}
            onSuccess={() => setShowTransferDialog(false)}
          />
        )}
      </PatientLayout>
  );
}
